import { useParams, useLocation } from 'react-router-dom';
import {
  IconLoader,
  usePrevious,
  PermissionEnum,
  PermissionDenied,
  ButtonPrimary,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { setMatchSelected } from './store/slices';
import NewApplicationInfoTop from '../common/new-application-info-top';
import { OnSubmitType, ApplicationType } from '../types';
import useQuestionnarie from './hooks/use-questionnarie';
import useApplication from './hooks/use-application';
import ApplicationSteps from './parts/steps';
import ApplicationWrapper from './parts/application-wrapper';

import cn from '../common/styles.module.css';

const NewApplicationInfo = () => {
  const { validators, applicationChat } = useAppSelector(
    (state) => state.newApplications.list,
  );
  const {
    newApplicationInfo,
    hintVisible,
    stepsStatus: applicationStepsStatus,
    systemLogs,
    documents,
    agreements,
    agreementsErrorsList,
    ongoingMonitoring,
    ongoingCheckingStatus,
    questionnarie,
    changeLogs,
    changeLogsPagination,
    riskScore,
    amlUpdateStatus,
    sourceOfWealth,
    sourceOfIncome,
    sourceOfWealthForIndividual,
    accountsCurrencies,
    questionnarieId,
    accountsFlows,
    expectedFundsOrigins,
    customerSourceOfFunds,
    crsAndFatca,
  } = useAppSelector((state) => state.newApplications.individualInfo);
  const { sourcesOfFunds, clientsActivities, infoDictionariesIsLoading } =
    useAppSelector((state) => state.customersDictionaries);
  const { activePermissions } = useAppSelector((state) => state.root);

  const { search } = useLocation();

  const dispatch = useAppDispatch();
  const { applicationId } = useParams();

  const prevApplicationId = usePrevious(applicationId);

  const {
    questionnarieFields,
    accountsCurrenciesValues,
    expectedFundsOriginsValues,
    accountsFlowsValues,
    crsAndFatcaValues,
    haveSecondPassport,
    questionnarieGetValues,
    questionnarieSetError,
    onSaveAccountsCurrencies,
    onSaveAccountsFlows,
    onSaveExpectedFundsOrigins,
    onSaveCRSAndFatca,
    questionnarieSetValue,
    defaultQuestionnarieFormValues,
    questionnarieIsFetching,
    questionnarieIsLoading,
    createAccountsCurrenciesIsProgress,
    createAccountsFlowsIsProgress,
    createExpectedFundsOriginsInProgress,
    updateAccountsCurrenciesInProgress,
    updateAccountsFlowsInProgress,
    updateExpectedFundsOriginsInProgress,
    createCrsAndFatcaIsProgress,
    updateCrsAndFatcaInProgress,
    questionnarieClearErrors,
    setHaveSecondPassport,
    questionnarieControl,
    questionnarieErrors,
    accountsCurrenciesIsLoading,
    accountsCurrenciesIsFetching,
    setAccountsCurrenciesValues,
    onDeleteAccountCurrencies,
    selectedAccountsCurrencyId,
    deleteAccountsCurrenciesInProgress,
    setAccountsFlowsValues,
    accountsFlowsIsLoading,
    accountsFlowsIsFetching,
    expectedFundsOriginsIsLoading,
    expectedFundsOriginsIsFetching,
    deleteExpectedFundsOriginsInProgress,
    onDeleteExpectedFundsOrigins,
    selectedExpectedFundsOriginsId,
    setExpectedFundsOriginsValues,
    selectedCrsAndFatcaId,
    crsAndFatcaIsLoading,
    crsAndFatcaIsFetching,
    deleteCrsAndFatcaInProgress,
    onDeleteCRSAndFatca,
    setCrsAndFatcaValues,
    questionnarieIsDirty,
  } = useQuestionnarie({
    crsAndFatca,
    questionnarieId,
    expectedFundsOrigins,
    accountsCurrencies,
    accountsFlows,
    newApplicationInfo,
    activePermissions,
    sourceOfWealthForIndividual,
    sourceOfIncome,
    sourceOfWealth,
    customerSourceOfFunds,
    questionnarie,
  });

  const {
    userCreated,
    isLoading,
    isFetching,
    applicationLoadErrorVisible,
    getApplicationDocuments,
    setStatusChangeError,
    setSuccessMessageVisible,
    onSubmit,
    requestInProgress,
    questionnarieUpdateInProgress,
    onCreateCustomer,
    createInProgress,
    fields,
    defaultFormValues,
    statusChangeError,
    setError,
    clearErrors,
    control,
    errors,
    setValue,
    isDirty,
    setDirtyPopupVisible,
    dirtyPopupVisible,
    successMessageVisible,
    updateError,
    updateQuestionnarieError,
    agreementsIsLoading,
    agreementsIsFetching,
  } = useApplication({
    validators,
    clientsActivities,
    sourcesOfFunds,
    applicationStepsStatus,
    agreements,
    ongoingMonitoring,
    ongoingCheckingStatus,
    newApplicationInfo,
    questionnarieFields,
    accountsCurrenciesValues,
    expectedFundsOriginsValues,
    accountsFlowsValues,
    crsAndFatcaValues,
    haveSecondPassport,
    documents,
    applicationId,
    activePermissions,
    questionnarieGetValues,
    questionnarieSetError,
    onSaveAccountsCurrencies,
    onSaveAccountsFlows,
    onSaveExpectedFundsOrigins,
    onSaveCRSAndFatca,
    prevApplicationId,
    amlUpdateStatus,
    questionnarieSetValue,
    defaultQuestionnarieFormValues,
  });

  let content = <div />;

  if (!userCreated && (isLoading || isFetching)) {
    content = (
      <div className={cn.newApplicationInfoContentLoader}>
        <IconLoader />
      </div>
    );
  } else if (
    (!isLoading &&
      !isFetching &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_LIST,
      )) ||
    userCreated
  ) {
    if (applicationLoadErrorVisible) {
      content = (
        <div className={cn.applicationLoadError}>
          <div className={cn.applicationLoadErrorTitle}>
            Something went wrong. Reload page
          </div>
          <ButtonPrimary
            text="Reload"
            className={cn.reloadButton}
            onClick={() => window.location.reload()}
            bold
          />
        </div>
      );
    } else {
      content = (
        <div className={cn.newApplicationInfoContent}>
          <NewApplicationInfoTop
            getApplicationDocuments={getApplicationDocuments}
            setStatusChangeError={setStatusChangeError}
            newApplicationInfo={newApplicationInfo}
            setSuccessMessageVisible={setSuccessMessageVisible}
            stepsStatus={applicationStepsStatus}
            setMatchSelected={(data) => dispatch(setMatchSelected(data))}
            applicationId={applicationId}
            onSubmit={onSubmit as OnSubmitType}
            requestInProgress={
              requestInProgress ||
              questionnarieUpdateInProgress ||
              createAccountsCurrenciesIsProgress ||
              createAccountsFlowsIsProgress ||
              createExpectedFundsOriginsInProgress ||
              updateAccountsCurrenciesInProgress ||
              updateAccountsFlowsInProgress ||
              updateExpectedFundsOriginsInProgress ||
              createCrsAndFatcaIsProgress ||
              updateCrsAndFatcaInProgress
            }
            onCreate={() => onCreateCustomer()}
            createInProgress={createInProgress}
            questionnarieFields={questionnarieFields}
            fields={fields}
            defaultFormValues={defaultFormValues}
            defaultQuestionnarieFormValues={defaultQuestionnarieFormValues}
            systemLogs={systemLogs}
            applicationType={ApplicationType.INDIVIDUAL}
            isEditable={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
            )}
            createAvailable={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_CREATE_ACTION,
            )}
            statusChangeError={statusChangeError}
            canSelectResponsible={activePermissions?.includes(
              PermissionEnum.USERS_LIST,
            )}
            title={
              newApplicationInfo
                ? `${newApplicationInfo.firstName} ${newApplicationInfo.lastName}`
                : 'New Application'
            }
            applicationChat={applicationChat ? applicationChat[0] : undefined}
            riskScore={riskScore}
            setError={setError}

            clearErrors={clearErrors}
            questionnarieSetError={questionnarieSetError}
            questionnarieClearErrors={questionnarieClearErrors}
            changeLogs={changeLogs}
            changeLogsPagination={changeLogsPagination}
            amlUpdateStatus={amlUpdateStatus}
            stepOneBlockLoadingInProgress={
              createAccountsCurrenciesIsProgress ||
              createAccountsFlowsIsProgress ||
              createExpectedFundsOriginsInProgress ||
              updateAccountsCurrenciesInProgress ||
              updateAccountsFlowsInProgress ||
              updateExpectedFundsOriginsInProgress ||
              createCrsAndFatcaIsProgress ||
              updateCrsAndFatcaInProgress
            }
            onSaveAccountsCurrencies={onSaveAccountsCurrencies}
            accountsCurrenciesValues={accountsCurrenciesValues
              ?.filter((item) => item.currencyId)
              ?.map((item) => ({
                currencyId: item.currencyId,
                accountOpeningPurpose: item.accountOpeningPurpose,
                plannedTransactionNature: item.plannedTransactionNature,
              }))}
            accountsCurrencies={accountsCurrencies?.map((item) => ({
              currencyId: item.currencyId,
              accountOpeningPurpose: item.accountOpeningPurpose,
              plannedTransactionNature: item.plannedTransactionNature,
            }))}
            setHaveSecondPassport={setHaveSecondPassport}
            haveSecondPassport={haveSecondPassport}
            onSaveAccountsFlows={onSaveAccountsFlows}
            accountsFlowsValues={accountsFlowsValues
              ?.filter((item) => item.currencyId)
              ?.map((item) => ({
                transactionType: item.transactionType,
                currencyId: item.currencyId,
                expectedAnnualCreditTurnover:
                  item.expectedAnnualCreditTurnover?.toString() || '',
                maxTransferAmountPerTransaction:
                  item.maxTransferAmountPerTransaction?.toString() || '',
                transactionFrequency:
                  item.transactionFrequency?.toString() || '',
                transactionFrequencyPeriodMonths:
                  item.transactionFrequencyPeriodMonths?.toString() || '',
              }))}
            accountsFlows={accountsFlows?.map((item) => ({
              transactionType: item.transactionType,
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover:
                item.expectedAnnualCreditTurnover?.toString() || '',
              maxTransferAmountPerTransaction:
                item.maxTransferAmountPerTransaction?.toString() || '',
              transactionFrequency: item.transactionFrequency?.toString() || '',
              transactionFrequencyPeriodMonths:
                item.transactionFrequencyPeriodMonths?.toString() || '',
            }))}
            onSaveExpectedFundsOrigins={onSaveExpectedFundsOrigins}
            expectedFundsOriginsValues={expectedFundsOriginsValues
              ?.filter((item) => item.currencyId)
              ?.map((item) => ({
                currencyId: item.currencyId,
                mainCounterpartiesAndWebsites:
                  item?.mainCounterpartiesAndWebsites || '',
                countryOfIncorporationId: item?.countryOfIncorporationId || '',
                counterpartyBankCountryIds:
                  item?.counterpartyBankCountryIds || [],
                activityCountryIds: item?.activityCountryIds || [],
                mainActivities: item?.mainActivities || '',
                transactionReason: item?.transactionReason || '',
                transactionType: item?.transactionType || '',
              }))}
            expectedFundsOrigins={expectedFundsOrigins?.map((item) => ({
              currencyId: item.currencyId,
              mainCounterpartiesAndWebsites:
                item?.mainCounterpartiesAndWebsites || '',
              countryOfIncorporationId: item?.countryOfIncorporationId || '',
              counterpartyBankCountryIds:
                item?.counterpartyBankCountryIds || [],
              activityCountryIds: item?.activityCountryIds || [],
              mainActivities: item?.mainActivities || '',
              transactionReason: item?.transactionReason || '',
              transactionType: item?.transactionType || '',
            }))}
            onSaveCrsAndFatca={onSaveCRSAndFatca}
            crsAndFatcaValues={crsAndFatcaValues
              ?.filter((item) => item.residenceCountryId)
              ?.map((item) => ({
                residenceCountryId: item?.residenceCountryId || '',
                taxIdentificationNumber: item?.taxIdentificationNumber || '',
                noTinReason: item?.noTinReason,
              }))}
            crsAndFatca={crsAndFatca?.map((item) => ({
              residenceCountryId: item?.residenceCountryId,
              taxIdentificationNumber: item?.taxIdentificationNumber || '',
              noTinReason: item?.noTinReason || '',
            }))}
            updateError={updateError}
            updateQuestionnarieError={updateQuestionnarieError}
            infoDictionariesIsLoading={
              infoDictionariesIsLoading ||
              questionnarieIsLoading ||
              (!questionnarie && questionnarieIsFetching)
            }
          />
          <ApplicationSteps
            applicationStepsStatus={applicationStepsStatus}
            control={control}
            questionnarieControl={questionnarieControl}
            errors={errors}
            questionnarieErrors={questionnarieErrors}
            newApplicationInfo={newApplicationInfo}
            fields={fields}
            activePermissions={activePermissions}
            clearErrors={clearErrors}
            questionnarieClearErrors={questionnarieClearErrors}
            questionnarieFields={questionnarieFields}
            sourceOfWealth={sourceOfWealth}
            questionnarieSetValue={questionnarieSetValue}
            sourceOfIncome={sourceOfIncome}
            sourceOfWealthForIndividual={sourceOfWealthForIndividual}
            documents={documents}
            accountsCurrenciesIsLoading={accountsCurrenciesIsLoading}
            accountsCurrenciesIsFetching={accountsCurrenciesIsFetching}
            accountsCurrenciesValues={accountsCurrenciesValues}
            setAccountsCurrenciesValues={setAccountsCurrenciesValues}
            onDeleteAccountCurrencies={onDeleteAccountCurrencies}
            selectedAccountsCurrencyId={selectedAccountsCurrencyId}
            deleteAccountsCurrenciesInProgress={
              deleteAccountsCurrenciesInProgress
            }
            setAccountsFlowsValues={setAccountsFlowsValues}
            accountsFlowsValues={accountsFlowsValues}
            customerSourceOfFunds={customerSourceOfFunds}
            accountsFlowsIsLoading={accountsFlowsIsLoading}
            accountsFlowsIsFetching={accountsFlowsIsFetching}
            expectedFundsOriginsIsLoading={expectedFundsOriginsIsLoading}
            expectedFundsOriginsIsFetching={expectedFundsOriginsIsFetching}
            deleteExpectedFundsOriginsInProgress={
              deleteExpectedFundsOriginsInProgress
            }
            onDeleteExpectedFundsOrigins={onDeleteExpectedFundsOrigins}
            selectedExpectedFundsOriginsId={selectedExpectedFundsOriginsId}
            expectedFundsOriginsValues={expectedFundsOriginsValues}
            setExpectedFundsOriginsValues={setExpectedFundsOriginsValues}
            search={search}
            applicationId={applicationId}
            selectedCrsAndFatcaId={selectedCrsAndFatcaId}
            crsAndFatcaIsLoading={crsAndFatcaIsLoading}
            crsAndFatcaIsFetching={crsAndFatcaIsFetching}
            deleteCrsAndFatcaInProgress={deleteCrsAndFatcaInProgress}
            onDeleteCRSAndFatca={onDeleteCRSAndFatca}
            crsAndFatcaValues={crsAndFatcaValues}
            setCrsAndFatcaValues={setCrsAndFatcaValues}
            setHaveSecondPassport={setHaveSecondPassport}
            haveSecondPassport={haveSecondPassport}
            setValue={setValue}
            ongoingCheckingStatus={ongoingCheckingStatus}
            ongoingMonitoring={ongoingMonitoring}
            agreements={agreements}
            agreementsIsLoading={agreementsIsLoading || agreementsIsFetching}
            infoDictionariesIsLoading={infoDictionariesIsLoading}
            questionnarieIsLoading={
              questionnarieIsLoading ||
              (!questionnarie && questionnarieIsFetching)
            }
            agreementsErrorsList={agreementsErrorsList}
          />
        </div>
      );
    }
  } else {
    // eslint-disable-next-line no-lonely-if
    if (!userCreated) {
      content = <PermissionDenied />;
    }
  }

  return (
    <ApplicationWrapper
      isDirty={isDirty}
      questionnarieIsDirty={questionnarieIsDirty}
      setDirtyPopupVisible={setDirtyPopupVisible}
      hintVisible={hintVisible}
      newApplicationInfo={newApplicationInfo}
      dirtyPopupVisible={dirtyPopupVisible}
      successMessageVisible={successMessageVisible}
    >
      {content}
    </ApplicationWrapper>
  );
};

export default NewApplicationInfo;
